/**
 * Client for the Sketchling API (apps/server, /api/v1). The app is local-first: every call may
 * fail (offline) and callers treat that as "try later".
 */
import { DEFAULT_CONFIG, mergeCreatureLists, sanitizeItemStickers, type CreatureMeta, type Feeling, type LiveConfig, type ProgressState, type Tier, type BillingPeriod } from '@sketchling/core';
import { isNative } from './platform';
import { appNow, useApp, APP_BUILD, type Entitlement, type LocalProfile } from '../state/store';
import { getImage, putImage } from './idb';
import type { JournalItem, JournalSource } from '../state/store';
import { useCreatureStore } from '../components/living/creatures';

/** Web uses the Vite proxy (/api -> :8790). Native builds need VITE_API_URL. */
export const API_ORIGIN = (import.meta.env.VITE_API_URL as string | undefined) || (isNative ? 'https://api.sketchling.app' : '');
const BASE = API_ORIGIN + '/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Requests give up after this long (a stalled connection must never block sync forever); uploads get longer. */
export const REQUEST_TIMEOUT_MS = 15_000;
const UPLOAD_TIMEOUT_MS = 60_000;

export async function request<T>(path: string, init: RequestInit & { auth?: boolean; timeoutMs?: number } = {}, retry = true): Promise<T> {
  const { account } = useApp.getState();
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof Blob)) headers.set('Content-Type', 'application/json');
  if (init.auth !== false && account.accessToken) headers.set('Authorization', `Bearer ${account.accessToken}`);
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const ms = init.timeoutMs ?? (init.body instanceof Blob ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS);
  const timer = ctrl ? setTimeout(() => ctrl.abort(), ms) : null;
  if (init.signal && ctrl) init.signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  let res: Response;
  try {
    res = await fetch(path.startsWith('http') ? path : `${BASE}${path}`, { ...init, headers, ...(ctrl ? { signal: ctrl.signal } : {}) });
  } catch {
    throw new ApiError(0, 'offline', 'offline');
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (res.status === 401 && retry && account.refreshToken) {
    if (await refresh()) return request<T>(path, init, false);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new ApiError(res.status, body.error ?? 'error', body.message ?? res.statusText);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Cleanups that need the session before sign-out (e.g. unregistering this device from push). */
const beforeLogout = new Set<() => Promise<void>>();
export const onBeforeLogout = (fn: () => Promise<void>) => void beforeLogout.add(fn);

export interface ServerUser {
  id: string;
  email: string | null;
  provider: string;
  anonymous: boolean;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: ServerUser;
  created?: boolean;
}

async function refresh(): Promise<boolean> {
  const { account, setAccount } = useApp.getState();
  try {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: account.refreshToken }),
    });
    if (!r.ok) {
      setAccount({});
      return false;
    }
    const data = (await r.json()) as AuthResponse;
    setAccount({ ...useApp.getState().account, accessToken: data.accessToken, refreshToken: data.refreshToken });
    return true;
  } catch {
    return false;
  }
}

function storeAuth(r: AuthResponse) {
  const prev = useApp.getState().account;
  useApp.getState().setAccount({
    ...prev,
    accessToken: r.accessToken,
    refreshToken: r.refreshToken,
    userId: r.user.id,
    email: r.user.email,
    anonymous: r.user.anonymous,
  });
}

export interface ServerProfile {
  id: string;
  name: string;
  avatar: string;
  age: number | null;
  signatureFont: string | null;
  favoriteSubjects: string[];
  isRealDrawer: boolean;
  access: 'full' | 'limited';
  createdAt: string;
}

export interface ServerEntitlement {
  tier: Tier;
  plan: Tier | null;
  period: BillingPeriod | null;
  status: string | null;
  isTrial: boolean;
  expiresAt: string | null;
  trialEndsAt: string | null;
  maxProfiles: number;
}

export interface ServerPending {
  plan: 'individual' | 'family';
  period: BillingPeriod;
  since: string;
  store: string;
}

export interface DrawingRecord {
  id: string;
  profileId: string;
  title: string;
  subject: string;
  feeling: number | null;
  description?: string;
  meta?: Record<string, unknown>;
  createdAt: string;
  uploaded: boolean;
}

export interface ShareComment {
  id: string;
  commenter: string;
  name?: string | null;
  award: string;
  text: string;
  createdAt: string;
}

const profileBody = (p: LocalProfile) => ({
  name: p.name,
  avatar: p.avatar,
  age: p.age,
  signatureFont: p.signatureFont,
  favoriteSubjects: p.favoriteSubjects,
  isRealDrawer: p.isRealDrawer,
});

export const api = {
  /* auth */
  async anonymous() {
    const r = await request<AuthResponse>('/auth/anonymous', { method: 'POST', body: JSON.stringify({}) });
    storeAuth(r);
    return r;
  },
  emailStart: (email: string) =>
    request<{ sent: boolean; devCode?: string }>('/auth/email/start', { method: 'POST', body: JSON.stringify({ email, lang: useApp.getState().settings.language }) }),
  /** Google / Apple ID token → session (an anonymous caller is upgraded in place). */
  async oauth(provider: 'google' | 'apple', idToken: string, nonce?: string, name?: string) {
    const r = await request<AuthResponse>('/auth/oauth', { method: 'POST', body: JSON.stringify({ provider, idToken, nonce, name }) });
    storeAuth(r);
    return r;
  },
  async emailVerify(email: string, code: string) {
    const r = await request<AuthResponse>('/auth/email/verify', { method: 'POST', body: JSON.stringify({ email, code }) });
    storeAuth(r);
    return r;
  },
  async login(email: string, password: string) {
    const r = await request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }), auth: false });
    storeAuth(r);
    return r;
  },
  async logout() {
    await Promise.all([...beforeLogout].map((fn) => fn().catch(() => undefined)));
    const { account, setAccount } = useApp.getState();
    await request('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken: account.refreshToken }) }).catch(() => undefined);
    setAccount({});
  },
  changeEmailStart: (email: string) =>
    request<{ sent: boolean; devCode?: string }>('/me/email/start', { method: 'POST', body: JSON.stringify({ email, lang: useApp.getState().settings.language }) }),
  /** Account language (server emails / push / share pages). */
  setLanguage: (lang: string) => request<{ ok: boolean }>('/me', { method: 'PATCH', body: JSON.stringify({ lang }) }),
  changeEmailVerify: (email: string, code: string) => request<{ user: ServerUser }>('/me/email/verify', { method: 'POST', body: JSON.stringify({ email, code }) }),

  /* me / profiles */
  me: () => request<{ user: ServerUser; profiles: ServerProfile[]; entitlement: ServerEntitlement; flags: { unlockAll: boolean } }>('/me'),
  deleteAccount: () => request<void>('/me', { method: 'DELETE' }),
  createProfile: (p: LocalProfile) => request<{ profile: ServerProfile }>('/profiles', { method: 'POST', body: JSON.stringify(profileBody(p)) }).then((r) => r.profile),
  updateProfile: (id: string, p: LocalProfile) => request<{ profile: ServerProfile }>(`/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(profileBody(p)) }),
  deleteProfile: (id: string) => request<void>(`/profiles/${id}`, { method: 'DELETE' }),
  markPremium: (id: string) => request<unknown>(`/profiles/${id}/mark-premium`, { method: 'POST', body: JSON.stringify({}) }),
  getProgress: (id: string) => request<{ progress: ProgressState }>(`/profiles/${id}/progress`),
  putProgress: (id: string, progress: ProgressState) => request<{ progress: ProgressState }>(`/profiles/${id}/progress`, { method: 'PUT', body: JSON.stringify({ progress }) }),

  /* drawings (signed upload / download urls) */
  uploadUrl: (body: { profileId: string; contentType: string; title?: string; description?: string; subject?: string; feeling?: number | null; meta?: Record<string, unknown> }) =>
    request<{ drawingId: string; uploadUrl: string; expiresAt: string }>('/drawings/upload-url', { method: 'POST', body: JSON.stringify(body) }),
  async putBlob(uploadUrl: string, blob: Blob) {
    const res = await fetch(API_ORIGIN + uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type || 'image/jpeg' } });
    if (!res.ok) throw new ApiError(res.status, 'upload_failed', 'upload failed');
  },
  listDrawings: (profileId: string) => request<{ drawings: DrawingRecord[] }>(`/drawings?profileId=${encodeURIComponent(profileId)}`),
  downloadUrl: (id: string) => request<{ url: string; expiresAt: string }>(`/drawings/${id}/download-url`),
  deleteDrawing: (id: string) => request<void>(`/drawings/${id}`, { method: 'DELETE' }),
  patchDrawing: (id: string, body: { title?: string; description?: string; feeling?: number | null; meta?: Record<string, unknown> }) =>
    request<{ drawing: DrawingRecord }>(`/drawings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  drawingComments: (id: string) => request<{ comments: ShareComment[] }>(`/drawings/${id}/comments`),

  /* shares */
  authProviders: () => request<{ providers: string[]; devBypass: boolean; email?: boolean }>('/auth/providers', { auth: false }),
  createShare: (drawingId: string) => request<{ shareId: string; url: string }>('/shares', { method: 'POST', body: JSON.stringify({ drawingId }) }),
  deleteShare: (shareId: string) => request<void>(`/shares/${encodeURIComponent(shareId)}`, { method: 'DELETE' }),
  publicShare: (id: string) =>
    request<{ shareId: string; title: string; artist: string; commentText?: boolean; createdAt: string; imageUrl: string; comments: ShareComment[] }>(`/public/shares/${id}`, { auth: false }),
  publicComment: (id: string, body: { commenter: string; name?: string; award: string; text?: string }) =>
    request<unknown>(`/public/shares/${id}/comments`, { method: 'POST', body: JSON.stringify(body), auth: false }),

  /* config / billing / feedback */
  config: (q: Record<string, string>) =>
    request<{ config: LiveConfig; buckets: Record<string, string>; appUpdate: 'none' | 'optional' | 'force'; unlockAll?: boolean }>(`/config?${new URLSearchParams(q)}`),
  plans: () => request<{ trialDays: number; trialReminderDay: number; plans: { plan: string; period: string; priceCents: number }[] }>('/billing/plans'),
  mockPurchase: (plan: 'individual' | 'family', period: BillingPeriod) =>
    request<{ entitlement: ServerEntitlement }>('/billing/mock-purchase', { method: 'POST', body: JSON.stringify({ plan, period, adultVerified: true }) }),
  restore: () => request<{ entitlement: ServerEntitlement; pending?: ServerPending | null }>('/billing/restore', { method: 'POST', body: JSON.stringify({}) }),
  mockAskToBuy: (plan: 'individual' | 'family', period: BillingPeriod) =>
    request<{ entitlement: ServerEntitlement; pending: ServerPending | null }>('/billing/mock-purchase', { method: 'POST', body: JSON.stringify({ plan, period, adultVerified: true, askToBuy: true }) }),
  mockResolve: (approve: boolean) =>
    request<{ entitlement: ServerEntitlement; pending: ServerPending | null }>('/billing/mock-resolve', { method: 'POST', body: JSON.stringify({ approve }) }),
  billingConfig: () => request<{ mock: boolean; stripe: boolean; revenuecat: { webhook: boolean; sync: boolean } }>('/billing/config', { auth: false }),
  checkout: (plan: 'individual' | 'family', period: BillingPeriod) => request<{ id: string; url: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan, period }) }),
  stripeConfirm: (sessionId: string) =>
    request<{ status: string; paymentStatus: string; entitlement: ServerEntitlement }>('/billing/stripe/confirm', { method: 'POST', body: JSON.stringify({ sessionId }) }),
  revenuecatSync: () => request<{ synced: number; entitlement: ServerEntitlement }>('/billing/revenuecat/sync', { method: 'POST', body: JSON.stringify({}) }),

  /* push */
  pushConfig: () => request<{ vapidPublicKey: string | null; senders: Record<string, string> }>('/push/config', { auth: false }),
  pushRegister: (body: Record<string, unknown>) => request<{ device: { id: string } }>('/push/devices', { method: 'POST', body: JSON.stringify(body) }),
  pushUnregister: (token: string) => request<void>('/push/devices', { method: 'DELETE', body: JSON.stringify({ token }) }),
  pushTest: () => request<{ results: { ok: boolean }[] }>('/push/test', { method: 'POST', body: JSON.stringify({}) }),
  voiceUploadUrl: (body: { kind: 'journalSurvey'; contentType: string; durationMs: number; profileId?: string }) =>
    request<{ voiceId: string; uploadUrl: string; expiresAt: string; maxBytes: number }>('/feedback/voice/upload-url', { method: 'POST', body: JSON.stringify(body) }),
  async putVoice(uploadUrl: string, blob: Blob, contentType: string) {
    const res = await fetch(API_ORIGIN + uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
    if (!res.ok) throw new ApiError(res.status, 'upload_failed', 'upload failed');
  },
  voiceSubmit: (body: { voiceId: string; text?: string }) => request<{ id: string; voiceId: string }>('/feedback/voice/submit', { method: 'POST', body: JSON.stringify(body) }),
  feedback: (kind: string, payload: Record<string, unknown>, profileId?: string) =>
    request<{ id: string }>('/feedback', { method: 'POST', body: JSON.stringify({ kind, payload, profileId }) }),
};

/* ----------------------------------- Helpers ----------------------------------- */

export function entitlementFromServer(e: ServerEntitlement, prev: Entitlement): Entitlement {
  if (e.tier === 'free') return prev.source === 'server' ? { tier: 'free' } : prev;
  return {
    tier: e.tier,
    period: e.period ?? undefined,
    expiresAt: e.expiresAt ?? undefined,
    trialEndsAt: e.trialEndsAt ?? undefined,
    source: 'server',
    premiumProfileId: prev.premiumProfileId,
  };
}

/** Ensure there is an account (silent anonymous guest) so sync and journal uploads work. */
export async function ensureAccount(): Promise<boolean> {
  if (useApp.getState().account.accessToken) return true;
  try {
    await api.anonymous();
    return true;
  } catch {
    return false;
  }
}

/** Fetch live config + experiment buckets. */
export async function refreshConfig(): Promise<void> {
  const s = useApp.getState();
  const active = s.profiles.find((p) => p.id === s.activeProfileId);
  const q: Record<string, string> = {
    build: String(APP_BUILD),
    lang: s.settings.language,
    device: typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) >= 600 ? 'tablet' : 'phone',
  };
  if (active?.age) q.age = String(active.age);
  const dbg = Object.entries(s.cheats.debugBuckets);
  if (dbg.length) q.buckets = dbg.map(([k, v]) => `${k}:${v}`).join(',');
  try {
    const r = await api.config(q);
    s.setConfig({ ...DEFAULT_CONFIG, ...r.config }, r.buckets);
    s.setAppUpdate(r.appUpdate);
  } catch {
    /* keep cached config */
  }
}

let syncing = false;

/**
 * Two-way sync: create missing server profiles, merge progress both ways, refresh entitlement and
 * tester flags, and upload journal photos that are not in the cloud yet.
 */
export async function syncAll(): Promise<void> {
  if (syncing) return;
  const s0 = useApp.getState();
  if (!s0.account.accessToken) return;
  syncing = true;
  try {
    const me = await api.me();
    const st = useApp.getState();
    st.setAccount({ ...st.account, unlockAll: me.flags.unlockAll, email: me.user.email, anonymous: me.user.anonymous });
    st.setEntitlement(entitlementFromServer(me.entitlement, st.entitlement));

    // Server-only profiles (e.g. after signing in on a new device) become local profiles.
    for (const rp of me.profiles) {
      if (!useApp.getState().profiles.some((p) => p.remoteId === rp.id)) {
        const local = useApp.getState().profiles.find((p) => !p.remoteId && p.name === rp.name);
        if (local) useApp.getState().updateProfile(local.id, { remoteId: rp.id });
        else
          useApp.getState().addProfile({
            remoteId: rp.id,
            name: rp.name,
            avatar: rp.avatar,
            age: rp.age,
            signatureFont: rp.signatureFont ?? 'Pacifico',
            favoriteSubjects: rp.favoriteSubjects ?? [],
            isRealDrawer: rp.isRealDrawer,
          });
      }
    }
    for (const local of useApp.getState().profiles) {
      let remoteId = local.remoteId;
      if (!remoteId) {
        try {
          remoteId = (await api.createProfile(local)).id;
          useApp.getState().updateProfile(local.id, { remoteId });
        } catch {
          continue; // profile limit or offline
        }
      }
      const merged = await api.putProgress(remoteId, useApp.getState().progress[local.id]!);
      useApp.getState().mergeRemoteProgress(local.id, merged.progress);
      await syncCreatures(local.id, remoteId).catch(() => undefined);
    }
    await uploadPendingDrawings();
    await restoreDrawings();
  } catch {
    /* offline: local-first */
  } finally {
    syncing = false;
  }
}

type RemoteCreature = CreatureMeta & { hasImage?: boolean };

/**
 * Living drawings sync (device-first, HANDOFF nit 9): send this device's creatures + deletions, take the merged list
 * back (core `mergeCreatureLists` on the server), upload the PNGs the cloud is missing and download the ones this
 * device is missing (a new device). Offline or any error: nothing changes on the device.
 */
export async function syncCreatures(localProfileId: string, remoteId: string): Promise<void> {
  const store = useCreatureStore.getState();
  const mine = store.creatures.filter((c) => c.profileId === localProfileId);
  const res = await request<{ creatures: RemoteCreature[]; removed: string[]; missingImages: string[] }>(`/profiles/${remoteId}/creatures`, {
    method: 'PUT',
    body: JSON.stringify({ creatures: mine, removed: store.removed ?? [] }),
  });
  const local = new Map(mine.map((c) => [c.id, c]));
  // PNGs the cloud does not have yet (best effort, one by one).
  for (const id of res.missingImages) {
    const c = local.get(id);
    const png = c ? await getImage(c.imageKey) : null;
    if (png) await request(`/profiles/${remoteId}/creatures/${id}/image`, { method: 'PUT', body: new Blob([png], { type: 'image/png' }) }).catch(() => undefined);
  }
  const list: CreatureMeta[] = [];
  for (const r of res.creatures) {
    const { hasImage, ...meta } = r;
    const have = local.get(r.id);
    if (have) {
      list.push({ ...have, name: meta.name, plays: Math.max(have.plays ?? 0, meta.plays ?? 0) });
      continue;
    }
    if (!hasImage) continue; // another device has not uploaded its PNG yet: next sync
    try {
      const { png } = await request<{ png: string }>(`/profiles/${remoteId}/creatures/${r.id}/image`);
      const bytes = Uint8Array.from(atob(png), (ch) => ch.charCodeAt(0));
      const imageKey = `creature-${r.id}`;
      await putImage(imageKey, new Blob([bytes], { type: 'image/png' }));
      list.push({ ...meta, profileId: localProfileId, imageKey });
    } catch {
      /* try again next sync */
    }
  }
  // Creatures made on this device while the request was in flight stay.
  const now = useCreatureStore.getState().creatures.filter((c) => c.profileId === localProfileId && !list.some((x) => x.id === c.id) && !res.removed.includes(c.id) && !res.creatures.some((x) => x.id === c.id));
  useCreatureStore.getState().replaceProfileCreatures(localProfileId, mergeCreatureLists(list, now));
}

/**
 * Bring cloud copies of journal drawings back to this device (sign-in on a new device, or after sign-out
 * cleared local data). Items already present (by remoteId or original client id) are skipped.
 */
export async function restoreDrawings(): Promise<void> {
  for (const profile of useApp.getState().profiles) {
    if (!profile.remoteId) continue;
    let rows: DrawingRecord[];
    try {
      rows = (await api.listDrawings(profile.remoteId)).drawings;
    } catch {
      return;
    }
    for (const r of rows) {
      const journal = useApp.getState().journal;
      const clientId = typeof r.meta?.clientId === 'string' ? r.meta.clientId : undefined;
      if (!r.uploaded || journal.some((j) => j.remoteId === r.id || (clientId && j.id === clientId))) continue;
      try {
        const { url } = await api.downloadUrl(r.id);
        const res = await fetch(API_ORIGIN + url);
        if (!res.ok) continue;
        const id = clientId ?? `cloud-${r.id}`;
        const imageKey = `img-${id}`;
        await putImage(imageKey, await res.blob());
        const source = typeof r.meta?.source === 'string' ? (r.meta.source as JournalSource) : 'upload';
        useApp.getState().addJournalItem({
          id,
          profileId: profile.id,
          imageKey,
          createdAt: r.createdAt,
          title: r.title,
          description: r.description ?? '',
          feeling: (r.feeling ?? null) as Feeling | null,
          filter: 'none',
          drawingId: typeof r.meta?.drawingId === 'string' ? r.meta.drawingId : undefined,
          subject: r.subject,
          source,
          comments: [],
          remoteId: r.id,
          stickers: sanitizeItemStickers(r.meta?.stickers),
        });
      } catch {
        /* try again on the next sync */
      }
    }
  }
}

/** Journal metadata kept with the cloud copy (stickers ride along so they follow the drawing to other devices). */
export function drawingMeta(item: JournalItem): Record<string, unknown> {
  return { source: item.source, drawingId: item.drawingId, clientId: item.id, at: item.createdAt, ...(item.stickers?.length ? { stickers: item.stickers } : {}) };
}

/** Upload journal photos without a cloud copy (offline-first journal sync). */
export async function uploadPendingDrawings(): Promise<void> {
  const st = useApp.getState();
  for (const item of st.journal) {
    if (item.remoteId || !item.imageKey) continue;
    const profile = st.profiles.find((p) => p.id === item.profileId);
    if (!profile?.remoteId) continue;
    const blob = await getImage(item.imageKey);
    if (!blob) continue;
    try {
      const r = await api.uploadUrl({
        profileId: profile.remoteId,
        contentType: blob.type || 'image/jpeg',
        title: item.title,
        description: item.description,
        subject: item.subject,
        feeling: item.feeling,
        meta: drawingMeta(item),
      });
      await api.putBlob(r.uploadUrl, blob);
      useApp.getState().updateJournalItem(item.id, { remoteId: r.drawingId });
    } catch {
      return;
    }
  }
}

/** Pull family compliments left on share links into the journal (external feedback). */
export async function fetchComments(): Promise<number> {
  const st = useApp.getState();
  let added = 0;
  for (const item of st.journal) {
    if (!item.remoteId || !item.shareId) continue;
    try {
      const { comments } = await api.drawingComments(item.remoteId);
      const known = new Set(item.comments.map((c) => c.id));
      const fresh = comments.filter((c) => !known.has(c.id));
      if (fresh.length) {
        added += fresh.length;
        useApp.getState().updateJournalItem(item.id, {
          comments: [
            ...item.comments,
            ...fresh.map((c) => ({ id: c.id, commenter: c.commenter as never, name: c.name ?? undefined, award: c.award as never, text: c.text, createdAt: c.createdAt, seen: false })),
          ],
        });
      }
    } catch {
      /* ignore */
    }
  }
  return added;
}
